import _ from 'lodash';
import { CreateArchiveSpecification } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/create-archive-specification';
import { IDestination } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/destination';
import { Job } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/job';

import { THEME } from '../../../constants/notification';
import { JobStatus, JobTypes } from '../../../redux/jobs/jobs.types';
import { getProjectByRoute } from '../../../redux/projects/projects.selectors';
import { Dispatch, GetState } from '../../../redux/redux.types';
import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';
import {
  createArchive,
  DEFAULT_ARCHIVE_ACL,
} from '../../../transport/services/archive';
import { Project } from '../../../types/entities';
import { MediaAsset } from '../../../types/media-assets';
import { errorLogger } from '../../../utils/error-logger/error-logger';
import { join } from '../../../utils/path/path';
import { BI_ERRORS } from '../../../utils/statistics/bi-error-logger/bi-errors';

import {
  JobOperationContext,
  OperationGroupType,
} from '../../operation-panel/operation-groups/operation-groups.types';
import { ArchiveDestination } from './archive-params-form/archive-params-form';
import { getErrorMessage } from './compress-flow.utils';

export const COMPRESS_FLOW_ACTION_NAMES = {
  START_FLOW: 'COMPRESS_FLOW_ACTION_NAMES.START_FLOW',
  CANCEL_FLOW: 'COMPRESS_FLOW_ACTION_NAMES.CANCEL_FLOW',
  COMPRESS_FILES_START: 'COMPRESS_FLOW_ACTION_NAMES.COMPRESS_FILES_START',
  COMPRESS_FILES_ERROR: 'COMPRESS_FLOW_ACTION_NAMES.COMPRESS_FILES_ERROR',
};

export const startCompressFlow = () => ({
  type: COMPRESS_FLOW_ACTION_NAMES.START_FLOW,
  payload: undefined,
});

export const cancelCompressFlow = () => ({
  type: COMPRESS_FLOW_ACTION_NAMES.CANCEL_FLOW,
  payload: undefined,
});

export interface CompressFilesPayload {
  batchClientId: string;
  sourceFileIds: string[];
}

export const startCompressFiles = (payload: CompressFilesPayload) => ({
  type: COMPRESS_FLOW_ACTION_NAMES.COMPRESS_FILES_START,
  payload,
});

export const errorCompressFiles = (error: any) => ({
  type: COMPRESS_FLOW_ACTION_NAMES.COMPRESS_FILES_ERROR,
  payload: error,
  notification: {
    message: getErrorMessage(_.get(error, 'code')),
    theme: THEME.ERROR,
  },
});

export function compressFiles(
  files: MediaAsset[],
  { name, destinationPath }: ArchiveDestination,
) {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const organizationId: string = getSelectedOrganizationId(state);
    const project: Project = getProjectByRoute(state);
    const batchClientId = _.uniqueId('compress_files_');
    const fileIds = _.map(files, 'id');
    const jobOperationContext: JobOperationContext = {
      operationType: OperationGroupType.MultipleDownload,
      groupId: batchClientId,
    };
    const destination: IDestination = {
      path: join(destinationPath, name),
      acl: DEFAULT_ARCHIVE_ACL,
    };

    dispatch(startCompressFiles({ batchClientId, sourceFileIds: fileIds }));

    dispatch({
      initiateJob: (): Promise<Job<CreateArchiveSpecification>> =>
        createArchive(organizationId, project, destination, fileIds),
      context: jobOperationContext,
    })
      .then(({ job }) => {
        if (
          job.status !== JobStatus.SUCCESS ||
          !job.result ||
          job.type !== JobTypes.ArchiveCreate
        ) {
          return;
        }
      })
      .catch((error) => {
        dispatch(errorCompressFiles(error));
        errorLogger.logError({
          biErrorDescription: BI_ERRORS.compressFlow.compressFiles,
          errorDescription: BI_ERRORS.compressFlow.compressFiles,
          otherParams: { error },
        });
      });
  };
}
